// ── <SignalLibraryDrawer> — Signal Library bottom drawer (two-pane) ──
//
// A bottom drawer (≤45vh) laid out as two panes:
//
//   ┌ DISCOVER SIGNALS ─────┬ YOUR SIGNAL LIBRARY ─────────────────────┐
//   │ [ add a signal │.CSV ]│ Select up to 2 signals…                   │
//   │ SUGGESTED (scrolls)   │ 3-col grid of saved signals (select)     │
//   └───────────────────────┴─────────────────────────────────────────┘
//
// LEFT (Discover): a free-text "add a signal" box with an attached Bulk .CSV
// upload, plus the agentic "Suggested for this question" list (scrollable, holds
// however many the server sends). RIGHT (Your Library): the saved signals as a
// selectable 3-col grid — clicking toggles a signal into the 2-slot chart
// selection (FIFO replace at 2, unchanged); ✕ removes it from the library.
//
// Layout reorg of the prior single-row drawer — the verify/add, CSV import,
// suggestion, selection (addSeries/removeSeries 2-slot FIFO), and drag-reorder
// behaviors are all preserved; only their arrangement changed. Chip styling still
// matches the IndicesBar so the drawer and bar feel like one surface.
//
// The drawer is ALWAYS rendered (no outer gate) — open/closed is driven by
// height/opacity/pointerEvents, not conditional mounting.
//
// Runtime values come from `ctx` (AppInner-scoped at render time) — see the
// destructure below; `setTsDrawerOpen` is used by the footer collapse chevron.

import React from "react";
import { ui, withIntent } from "../lib/telemetry/instrument.js";
import { emitSignalAdd } from "../lib/telemetry/uiIntents.js";
import { reserveSignals, signalsToAdd } from "../lib/series/libraryAdd.js";
import { C, themeColors, pickSeriesSlotColor } from "../lib/colors.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { SIGNAL_BAR_H } from "../lib/drawerStack.js";
import { RAIL_W } from "../lib/navRail.js";
import { surface } from "../lib/telemetry/instrument.js";
import { IconAlert, IconClose, IconSparkle } from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";

// Suggestion-section animations: a gentle pulse for the loading skeletons and a
// short rise+fade for cards as they land. Honors prefers-reduced-motion. Scoped
// class names so this can't collide with other styles.
const SUGGESTION_ANIM_CSS = `
@keyframes sug-pulse { 0%,100% { opacity: 0.3 } 50% { opacity: 0.65 } }
@keyframes sug-rise { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
.sug-skeleton { animation: sug-pulse 1.2s ease-in-out infinite; }
.sug-chip-enter { animation: sug-rise 0.26s ease both; }
@media (prefers-reduced-motion: reduce) {
  .sug-skeleton { animation: none; opacity: 0.5; }
  .sug-chip-enter { animation: none; }
}`;

// Two-slot chip palette per theme (matches the IndicesBar + the prior drawer):
// dev = gold, dusk = Dawn (action/accent), else = teal/orange.
// The 2-slot chip palette, derived from pickSeriesSlotColor (lib/colors.js) so
// the palette has a SINGLE source of truth — no drift if the slot colors change.
function slotColorsFor(theme) {
  return [pickSeriesSlotColor(theme, 0), pickSeriesSlotColor(theme, 1)];
}

// A pane's sentence-case heading.
function SectionLabel({ children, color, style }) {
  return (
    <div
      style={{
        fontSize: 13,
        color,
        letterSpacing: 0,
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function SignalLibraryDrawer({ ctx }) {
  const {
    theme,
    // Its place in the side-drawer stack: the top while it is open (useDrawerStack).
    zIndex = 70,
    // The open rail panel's width (a CSS length), or 0 with no panel open.
    railInset = 0,
    tsDrawerOpen,
    setTsDrawerOpen,
    manualQuery,
    setManualQuery,
    draggedChip,
    setDraggedChip,
    drawerOrder,
    setDrawerOrder,
    series,
    csvInputRef,
    verifySeries,
    addSeries,
    removeSeries,
    signalSuggestions,
    signalSuggestionsEnabled,
    signalSuggestionsLoading,
    signalHasQuestion,
  } = ctx;

  const slotColors = slotColorsFor(theme);
  const isDev = theme === "dev" || theme === "dusk";
  const seriesKeys = Object.keys(series);
  const hasText = manualQuery.trim().length > 0;

  // Click-outside-to-close: while the drawer is open, a mousedown anywhere outside
  // it closes it — EXCEPT on a drawer toggle ([data-drawer-toggle]): its own bar (which
  // owns the toggle, so matching here would close-then-reopen) and the other three
  // drawers' strips/bar. Opening or closing another drawer must not close this one; the
  // drawers stack (most recently opened on top) rather than replacing each other. This
  // used to key on the tour's `data-tour-spotlight="tab"` marker, which the Chat
  // Console strip happens not to carry — so closing the chat closed this drawer.
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    if (!tsDrawerOpen) return;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (e.target?.closest?.("[data-drawer-toggle]")) return;
      setTsDrawerOpen(false);
    };
    // telemetry-gesture: an outside click closes the drawer (dismissing)
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [tsDrawerOpen, setTsDrawerOpen]);

  // A signal already in the user's library (right pane). Once added, a suggestion
  // just LIVES in the library and drops out of the recommendations — no "Added" state.
  const isInLibrary = (name) =>
    drawerOrder.some(
      (d) =>
        (d.query || "").toLowerCase() === String(name).toLowerCase() ||
        (d.label || "").toLowerCase() === String(name).toLowerCase(),
    );
  const freshSuggestions = signalSuggestions.filter((s) => !isInLibrary(s.name));

  // The library as last rendered, for a handler that resumes after an await: the render-time
  // `drawerOrder` it closed over may be behind.
  const drawerOrderRef = React.useRef(drawerOrder);
  React.useEffect(() => {
    drawerOrderRef.current = drawerOrder;
  }, [drawerOrder]);
  /**
   * Save `entries` to the library, skipping any it already holds, and record `signal.add` with
   * the number actually added (none: no intent). The count is taken outside the state updater,
   * which React may run twice, against the library plus what earlier adds reserved. Returns
   * that number.
   */
  const addToLibrary = (entries, source, origin) => {
    // Reserved in the ref at once: another add resolving before React commits this one must
    // not count the same signals again (the next render's effect puts the saved list back).
    const added = reserveSignals(drawerOrderRef, entries);
    if (!added.length) return 0;
    emitSignalAdd(source, added.length, { origin });
    setDrawerOrder((prev) => {
      const next = signalsToAdd(prev, added);
      return next.length ? [...prev, ...next] : prev;
    });
    return added.length;
  };

  // ── LEFT: verify + save a free-text signal (Enter / the Add button). Runs the
  // 5-check verifier; a verified name is appended to the saved library (dedup). ──
  const submitManual = async () => {
    if (!hasText) return;
    const q = manualQuery.trim();
    const r = await verifySeries(q);
    if (!r.ok) return;
    addToLibrary([{ label: r.name, query: r.name }], "query", "signal-library.add");
    setManualQuery("");
  };

  // ── LEFT: bulk CSV import — parse candidate names, verify availability with a
  // small concurrency cap, append the verified ones to the saved library. ──
  const onCsvPicked = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so picking same file twice fires onChange
    if (!f) return;
    recordUiEvent({ event: "ui.indices.csv_picked", filename: f.name, size: f.size });
    let text = "";
    try {
      text = await f.text();
    } catch (err) {
      console.warn("[csv-import] could not read:", err.message);
      return;
    }
    // Lightweight CSV parse: split on newlines, then on common delimiters. Strip
    // surrounding quotes/whitespace, skip empty / numeric / date-like / header cells.
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const HEADER_WORDS = new Set([
      "ticker",
      "tickers",
      "symbol",
      "symbols",
      "name",
      "names",
      "index",
      "indices",
      "instrument",
      "instruments",
    ]);
    const candidates = [];
    const seen = new Set();
    for (const line of lines) {
      const cells = line.split(/[,;\t]/).map((c) => c.replace(/^["'\s]+|["'\s]+$/g, ""));
      for (const cell of cells) {
        if (!cell) continue;
        if (cell.length > 60) continue; // too long to be a ticker/name
        if (/^-?\d+(\.\d+)?%?$/.test(cell)) continue; // pure number
        if (/^[\d/.\-:]+$/.test(cell)) continue; // date-like
        const k = cell.toLowerCase();
        if (HEADER_WORDS.has(k)) continue; // header
        if (seen.has(k)) continue; // dedupe within file
        seen.add(k);
        candidates.push(cell);
      }
    }
    if (!candidates.length) return;
    // Dedupe against what's already saved.
    const inDrawer = new Set(
      drawerOrder.flatMap((i) => [i.query.toLowerCase(), i.label.toLowerCase()]),
    );
    const toResolve = candidates.filter((c) => !inDrawer.has(c.toLowerCase()));
    if (!toResolve.length) return;
    // Resolve + verify availability with a small concurrency cap.
    const CONCURRENCY = 4;
    const verified = [];
    const skipped = [];
    let i = 0;
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, toResolve.length) }, async () => {
        while (i < toResolve.length) {
          const c = toResolve[i++];
          const r = await verifySeries(c);
          if (r.ok) verified.push({ label: r.name, query: r.name });
          else skipped.push(c);
        }
      }),
    );
    if (!verified.length) return;
    const added = addToLibrary(verified, "csv", "signal-library.csv-file");
    recordUiEvent({
      event: "ui.indices.csv_imported",
      filename: f.name,
      added,
      skipped: skipped.length,
    });
  };

  // Clicking a saved-library signal toggles it into/out of the 2-slot selection.
  const toggleSignal = (idx, selectedIdx) => {
    if (selectedIdx >= 0) {
      removeSeries(seriesKeys[selectedIdx]);
    } else {
      // Clamp to the 2-slot palette (slot 0 or 1), same as addSuggestion/togglePlot.
      // A raw slotColors[seriesKeys.length] indexes slot 2 (undefined) once two are
      // plotted; addSeries then evicts to keep the chart at 2, so the new series
      // still lands in a real slot — color it to match instead of a stray default.
      const color = pickSeriesSlotColor(theme, Math.min(seriesKeys.length, 1));
      addSeries(idx.query, color, false, idx.ssid);
    }
  };

  return (
    <div
      ref={rootRef}
      data-testid="signal-library"
      {...surface("signal-library")}
      style={{
        position: "fixed",
        bottom: 32,
        // Between the two tab strips, which stack above every drawer.
        // Between the side tab strips, or beside the navigation rail.
        left: RAIL_W,
        right: 0,
        // Its slot in the side-drawer stack (most recently opened on top — see
        // lib/drawerStack.js); every slot is BELOW the signal-library bar (IndicesBar
        // SIGNAL_BAR_Z, which it slides up from behind) and the transient modals (z:400+).
        zIndex,
        background: C.sectionBg,
        borderTop: `1px solid ${C.edge}`,
        height: tsDrawerOpen ? "38vh" : 0,
        overflow: "hidden",
        transition: "height 0.28s ease, opacity 0.2s ease",
        opacity: tsDrawerOpen ? 1 : 0,
        pointerEvents: tsDrawerOpen ? "auto" : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{SUGGESTION_ANIM_CSS}</style>
      {/* The bar the drawer sits on runs only along the timeline column, so beside an open rail
         panel the drawer would stop a bar's height short of the window's foot and let the panel
         show through. This strip carries it down to the foot there: fixed, it escapes the
         root's clip but stays in the root's place in the drawer stack. */}
      {railInset ? (
        <div
          data-signal-library-foot=""
          style={{
            position: "fixed",
            left: RAIL_W,
            bottom: 0,
            width: railInset,
            height: SIGNAL_BAR_H,
            background: C.sectionBg,
          }}
        />
      ) : null}
      {/* Two panes fill the remaining height. */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* ── LEFT · DISCOVER SIGNALS ─────────────────────────────────────── */}
        <div
          style={{
            width: 340,
            flexShrink: 0,
            borderRight: `1px solid ${C.border}`,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: "12px 20px",
          }}
        >
          <SectionLabel color={C.white}>Suggested signals</SectionLabel>
          {signalHasQuestion ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: C.gray,
                marginTop: 4,
                marginBottom: 12,
              }}
            >
              <IconSparkle style={{ width: 12, height: 12, color: C.accent }} />
              Picked by {brandProduct.prose} for this question
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>
              Suggestions appear once you&apos;ve asked a question.
            </div>
          )}

          {/* Suggestions — scrollable card list (same visual language as the library
              chips; a + marks that clicking adds it to your library AND plots it, ✓
              once it's an active/plotted signal). Only when a question is active. */}
          {signalSuggestionsEnabled && signalHasQuestion && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                paddingRight: 4,
              }}
            >
              {signalSuggestionsLoading ? (
                [0, 1, 2, 3].map((i) => (
                  <div
                    key={`sk-${i}`}
                    className="sug-skeleton"
                    aria-hidden="true"
                    style={{
                      height: 48,
                      borderRadius: 6,
                      background: C.toolbarBtn,
                      border: `1px solid ${themeColors(theme).purple + "33"}`,
                      animationDelay: `${i * 120}ms`,
                    }}
                  />
                ))
              ) : freshSuggestions.length === 0 ? (
                // Keyed off freshSuggestions (what's rendered) — otherwise, once every
                // suggestion is already in the library, the list would render blank.
                <span style={{ fontSize: 12, color: C.gray, fontStyle: "italic" }}>
                  {signalSuggestions.length === 0
                    ? "No standout signals for this question."
                    : "All suggested signals are in your library."}
                </span>
              ) : (
                freshSuggestions.map((s, idx) => {
                  const nameLc = String(s.name).toLowerCase();
                  // A suggestion the system has already plotted (auto-select or a prior
                  // manual pick) counts as SELECTED — highlight it with the same slot
                  // color as its line on the timeline, mirroring the library chips.
                  const plottedIdx = seriesKeys.findIndex((k) => k.toLowerCase() === nameLc);
                  const plotted = plottedIdx >= 0;
                  const slotColor = slotColors[plottedIdx] || (isDev ? C.action : C.gray);
                  // Selected highlight — IDENTICAL to the library chips: dev/dusk fills
                  // with the slot color + dark ink; other themes keep the toolbar bg with
                  // a slot-color border + colored text. The "Add" button stays visible
                  // (it saves to the library) but flips to dark ink on a filled card.
                  const chipBg = isDev && plotted ? slotColor : C.toolbarBtn;
                  const chipBorder = plotted ? slotColor : C.border;
                  const nameInk = plotted ? (isDev ? C.bg : slotColor) : C.white;
                  const subInk = isDev && plotted ? C.bg : C.gray;
                  const addInk = isDev && plotted ? C.bg : C.grayL;
                  const addSuggestion = () => {
                    // Save to the library (→ it leaves this list) and plot it (2-slot cap).
                    addToLibrary(
                      [{ label: s.name, query: s.name, ssid: s.signalId }],
                      "suggestion",
                      "signal-library.suggestion-add",
                    );
                    if (!plotted) {
                      const color = pickSeriesSlotColor(theme, Math.min(seriesKeys.length, 1));
                      addSeries(s.name, color, false, s.signalId);
                    }
                  };
                  // Clicking the card body toggles the signal as one of the 2 plotted
                  // signals WITHOUT saving it to the library — so it stays in this list,
                  // just highlighted. (The "Add" button is the save-to-library action.)
                  const togglePlot = () => {
                    if (plotted) {
                      removeSeries(seriesKeys[plottedIdx]);
                    } else {
                      const color = pickSeriesSlotColor(theme, Math.min(seriesKeys.length, 1));
                      addSeries(s.name, color, false, s.signalId);
                    }
                  };
                  return (
                    // Non-interactive container — the two actions are real sibling
                    // buttons (plot + add) so no interactive control is nested inside
                    // another (a11y). The card fill/border highlight lives here.
                    <div
                      key={s.signalId || s.name}
                      title={s.reason || undefined}
                      className="sug-chip-enter"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: chipBg,
                        border: `1px solid ${chipBorder}`,
                        borderRadius: 6,
                        padding: "6px 10px",
                        flexShrink: 0,
                        animationDelay: `${idx * 40}ms`,
                      }}
                    >
                      {/* Plot toggle — the name + provenance IS the button (transparent,
                          borderless so it reads as plain text). Native <button> gives
                          Enter/Space + focus for free. */}
                      <button
                        {...ui("signal-library.suggestion-plot", s.signalId)}
                        type="button"
                        aria-pressed={plotted}
                        aria-label={`${plotted ? "Remove" : "Plot"} ${s.name}`}
                        onClick={togglePlot}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          minWidth: 0,
                          flex: 1,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          textAlign: "left",
                          cursor: "pointer",
                          fontFamily: C.font,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: nameInk,
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}
                        >
                          {s.name}
                        </span>
                        {s.sublabel || s.reason ? (
                          <span
                            style={{
                              fontSize: 11,
                              color: subInk,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {s.sublabel || s.reason}
                          </span>
                        ) : null}
                      </button>
                      {/* "Add →" box — saves the signal to your library (+ plots). Stays
                          visible even when the card is a selected/filled highlight; its ink
                          flips to dark on a filled card so it stays legible. */}
                      <button
                        {...ui("signal-library.suggestion-add", s.signalId)}
                        type="button"
                        onClick={addSuggestion}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexShrink: 0,
                          background: "transparent",
                          border: `1px solid ${addInk}`,
                          borderRadius: 6,
                          padding: "4px 12px",
                          color: addInk,
                          fontFamily: C.font,
                          fontSize: 12,
                          fontWeight: "bold",
                          letterSpacing: 0,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Add
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT · YOUR SIGNAL LIBRARY ─────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: "12px 24px",
          }}
        >
          {/* flex-start so the title top aligns with the taller entry bar's top (and
              with the left pane's DISCOVER SIGNALS title — both panes share a 12px top). */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            {/* Title + subtitle stacked so the subtitle sits directly under the title
                (marginTop 4) — vertically level with the left pane's subtitle, instead
                of dropping below the taller add-signal bar in this same row. */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <SectionLabel color={C.white}>Your signals</SectionLabel>
              <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>
                Choose up to two to show beside your forecast
              </div>
            </div>
            {/* Spacer right-justifies the add bar so its right edge lines up with the
                right edge of the signal grid below (both bounded by the pane padding). */}
            <div style={{ flex: 1 }} />
            {/* Add-a-signal bar. Add on Enter / Add; Bulk .CSV upload when empty. */}
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.inputBg,
                overflow: "hidden",
                width: 300,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingLeft: 10,
                  paddingRight: 6,
                  color: C.gray,
                  flexShrink: 0,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="20" y1="20" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                {...ui("signal-library.add-input")}
                value={manualQuery}
                onChange={(e) => setManualQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitManual();
                }}
                placeholder="Add a signal"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: "transparent",
                  border: "none",
                  color: C.white,
                  fontFamily: C.font,
                  fontSize: 12,
                  padding: "7px 6px",
                  outline: "none",
                }}
              />
              <div
                style={{ width: 1, background: C.border, alignSelf: "stretch", flexShrink: 0 }}
              />
              <button
                {...ui("signal-library.add", hasText ? "query" : "csv")}
                type="button"
                onClick={() => (hasText ? submitManual() : csvInputRef.current?.click())}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "0 12px",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  WebkitTapHighlightColor: "transparent",
                  color: hasText ? C.yellow : C.grayL,
                  fontFamily: C.font,
                  fontSize: 12,
                  fontWeight: "bold",
                  letterSpacing: 0,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {hasText ? (
                  "Add"
                ) : (
                  <>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Import CSV
                  </>
                )}
              </button>
            </div>
          </div>
          <input
            {...ui("signal-library.csv-file")}
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onCsvPicked}
            style={{ display: "none" }}
          />

          {/* Saved-signal grid — 3 columns; click to toggle into the 2-slot selection. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gridAutoRows: "min-content",
              gap: 10,
              alignContent: "start",
            }}
          >
            {drawerOrder.length === 0 ? (
              <div
                style={{ gridColumn: "1 / -1", fontSize: 12, color: C.gray, fontStyle: "italic" }}
              >
                No saved signals yet. Add one above, or pick a suggestion.
              </div>
            ) : (
              drawerOrder.map((idx) => {
                const selectedIdx = seriesKeys.findIndex(
                  (k) =>
                    k.toLowerCase() === idx.query.toLowerCase() ||
                    k.toLowerCase() === idx.label.toLowerCase(),
                );
                const isSel = selectedIdx >= 0;
                // A signal saved when it WAS available can stop resolving later (its
                // ssid was delisted / the catalog re-derived ids / signals-service
                // isn't configured here). fetchLiveSeries marks that; without this the
                // chip would fill in as "plotted" and just draw no line, with no
                // explanation — the stale-on-reload bug.
                const selEntry = isSel ? series[seriesKeys[selectedIdx]] : null;
                const isUnavailable = !!selEntry?.unavailable;
                const selColor = slotColors[selectedIdx] || (isDev ? C.action : C.gray);
                // Selected: solid slot-color fill + dark ink (dev/dusk) or colored
                // text (other themes). Unselected: GREY outline (per design).
                // Unavailable wins over selected: never present a dead signal as plotted.
                const chipBg = isDev && isSel && !isUnavailable ? selColor : C.toolbarBtn;
                const chipBorder = isUnavailable ? C.orange : isSel ? selColor : C.border;
                const chipInk = isUnavailable
                  ? C.orange
                  : isDev
                    ? isSel
                      ? C.bg
                      : C.grayL
                    : isSel
                      ? selColor
                      : C.gray;
                const isDrag = draggedChip === idx.query;
                // Remove from the saved library (and deselect it if plotted).
                const removeFromLibrary = () => {
                  withIntent(
                    "signal.remove",
                    () => undefined,
                    {},
                    {
                      origin: "signal-library.signal-remove",
                      surface: "signal-library",
                    },
                  );
                  if (isSel) removeSeries(seriesKeys[selectedIdx]);
                  setDrawerOrder((prev) => prev.filter((x) => x.query !== idx.query));
                };
                return (
                  // telemetry-gesture: dragging a chip reorders the library's display order
                  <div
                    key={idx.query}
                    draggable={true}
                    onDragStart={(e) => {
                      setDraggedChip(idx.query);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!draggedChip || draggedChip === idx.query) return;
                      setDrawerOrder((prev) => {
                        const next = prev.filter((x) => x.query !== draggedChip);
                        const tgt = next.findIndex((x) => x.query === idx.query);
                        const moving = prev.find((x) => x.query === draggedChip);
                        if (!moving || tgt < 0) return prev;
                        next.splice(tgt, 0, moving);
                        return next;
                      });
                    }}
                    onDragEnd={() => setDraggedChip(null)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      background: chipBg,
                      border: `1px solid ${chipBorder}`,
                      borderRadius: 6,
                      padding: "4px 8px",
                      cursor: isDrag ? "grabbing" : "grab",
                      userSelect: "none",
                      opacity: isDrag ? 0.4 : 1,
                      transition: "opacity 0.15s",
                      minWidth: 0,
                    }}
                  >
                    {/* Select toggle — the label IS the button (transparent/borderless so
                        it reads as plain text). Native <button> gives Enter/Space + focus;
                        the ✕ is a sibling button so no interactive control is nested. */}
                    <button
                      {...ui("signal-library.signal-toggle", idx.ssid)}
                      data-telemetry-redact=""
                      type="button"
                      // Tied to isSel, NOT to availability: the button is a toggle and
                      // an unavailable-but-selected chip still deselects on click, so
                      // reporting "not pressed" would contradict what the click does.
                      // Unavailability is conveyed by the aria-label, the ⚠ and the color.
                      aria-pressed={isSel}
                      aria-label={
                        isUnavailable
                          ? `${idx.label} — unavailable: ${selEntry.unavailableReason}`
                          : `${isSel ? "Deselect" : "Select"} ${idx.label}`
                      }
                      title={isUnavailable ? selEntry.unavailableReason : undefined}
                      onClick={() => toggleSignal(idx, selectedIdx)}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: "left",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        fontFamily: C.font,
                        fontSize: 12,
                        color: chipInk,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                    >
                      {isUnavailable ? (
                        <IconAlert
                          style={{ width: 12, height: 12, verticalAlign: -2, marginRight: 4 }}
                        />
                      ) : null}
                      {idx.label}
                    </button>
                    {/* ✕ removes the signal from the library (and deselects it). */}
                    <button
                      {...ui("signal-library.signal-remove", idx.ssid)}
                      data-telemetry-redact=""
                      type="button"
                      aria-label={`Remove ${idx.label} from library`}
                      onClick={removeFromLibrary}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        fontFamily: C.font,
                        fontSize: 12,
                        lineHeight: 1,
                        color: isDev && isSel ? chipInk : C.gray,
                        cursor: "pointer",
                        flexShrink: 0,
                        display: "inline-flex",
                      }}
                    >
                      <IconClose style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
